import { type RefObject, useEffect, useMemo, useReducer, useRef } from 'react';
import { backendClient } from '@/lib/api-client';
import { documentDisplayName } from '@/utils/file-utils';

interface CitationChunkInfoMetadata {
  document_guid: string;
  end_page_number?: number;
  page_number?: number;
  file_name?: string;
  display_name?: string;
  chunk_start_offset?: number;
  locator?: Record<string, unknown>;
  position_label?: string;
}

export type CitationChunkInfo = CitationChunkInfoMetadata & (
  | { chunk_id: number; segment_index?: never }
  | { segment_index: number; chunk_id?: never }
);

export type CitationChunkInfoRequest =
  | {
      documentGuid: string;
      kind: 'chunk';
      index: number;
    }
  | {
      documentGuid: string;
      kind: 'segment';
      index: number;
    };

const CHUNK_INFO_BATCH_DELAY_MS = 80;
const CHUNK_INFO_BATCH_SIZE = 500;
const CHUNK_INFO_STATUS_ATTR = 'data-chunk-info-status';
const CHUNK_INFO_READY = 'ready';
const CHUNK_INFO_MISSING = 'missing';
const chunkInfoCache = new Map<string, CitationChunkInfo | null>();
const inFlightChunkInfoKeys = new Set<string>();
const pendingChunkInfoBySlug = new Map<string, Map<string, CitationChunkInfoRequest>>();
const subscribersBySlug = new Map<string, Set<() => void>>();
let chunkInfoFlushTimer: ReturnType<typeof setTimeout> | null = null;

export function useCitationChunkInfo(
  slug: string | undefined,
  requests: CitationChunkInfoRequest[]
) {
  const [version, bumpVersion] = useReducer((value: number) => value + 1, 0);
  const requestSignature = requests
    .map(getRequestKey)
    .sort((first, second) => first.localeCompare(second))
    .join('|');
  // Callers rebuild the request list on every render; reuse the previous array
  // while the requested locators are unchanged so the returned Map stays stable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the request signature, not array identity.
  const stableRequests = useMemo(() => requests, [requestSignature]);

  useEffect(() => {
    if (!slug) return;
    return subscribeToChunkInfo(slug, bumpVersion);
  }, [slug]);

  useEffect(() => {
    if (!slug || stableRequests.length === 0) return;

    stableRequests.forEach((request) => {
      const cacheKey = getChunkInfoCacheKey(slug, request);
      if (chunkInfoCache.has(cacheKey) || inFlightChunkInfoKeys.has(cacheKey)) {
        return;
      }

      enqueueChunkInfo(slug, request);
    });
  }, [slug, stableRequests]);

  return useMemo(() => {
    // The cache lives outside React; subscriber updates advance this version.
    void version;
    const chunkInfoByRequestKey = new Map<string, CitationChunkInfo | null>();
    if (!slug) return chunkInfoByRequestKey;

    stableRequests.forEach((request) => {
      const cacheKey = getChunkInfoCacheKey(slug, request);
      if (chunkInfoCache.has(cacheKey)) {
        chunkInfoByRequestKey.set(getRequestKey(request), chunkInfoCache.get(cacheKey) ?? null);
      }
    });

    return chunkInfoByRequestKey;
  }, [slug, stableRequests, version]);
}

export function useCitationTooltips(
  slug: string | undefined,
  containerRef: RefObject<HTMLElement | null>
) {
  const processTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!slug || !containerRef.current) return;

    const container = containerRef.current;
    let isMounted = true;

    const processCitations = () => {
      if (!isMounted) return;

      const citations = container.querySelectorAll<HTMLElement>(
        `.citation-link[data-doc-guid]:is([data-chunk-id], [data-segment-index]):not([${CHUNK_INFO_STATUS_ATTR}="${CHUNK_INFO_READY}"]):not([${CHUNK_INFO_STATUS_ATTR}="${CHUNK_INFO_MISSING}"])`
      );

      citations.forEach((citation) => {
        const docGuid = citation.getAttribute('data-doc-guid');
        const chunkId = Number.parseInt(citation.getAttribute('data-chunk-id') || '', 10);
        const segmentIndex = Number.parseInt(citation.getAttribute('data-segment-index') || '', 10);
        if (!docGuid || (!Number.isFinite(chunkId) && !Number.isFinite(segmentIndex))) return;

        const request: CitationChunkInfoRequest = {
          documentGuid: docGuid,
          ...(Number.isFinite(segmentIndex)
            ? { kind: 'segment', index: segmentIndex }
            : { kind: 'chunk', index: chunkId }),
        };
        const cacheKey = getChunkInfoCacheKey(slug, request);
        if (chunkInfoCache.has(cacheKey)) {
          applyChunkInfoToCitation(citation, chunkInfoCache.get(cacheKey));
          return;
        }

        enqueueChunkInfo(slug, request);
      });
    };

    const scheduleProcessCitations = (delay = 0) => {
      if (processTimerRef.current) return;

      processTimerRef.current = setTimeout(() => {
        processTimerRef.current = null;
        processCitations();
      }, delay);
    };

    const unsubscribe = subscribeToChunkInfo(slug, () => {
      scheduleProcessCitations();
    });

    const observer = new MutationObserver(() => {
      scheduleProcessCitations(80);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    scheduleProcessCitations();

    return () => {
      isMounted = false;
      unsubscribe();
      if (processTimerRef.current) {
        clearTimeout(processTimerRef.current);
        processTimerRef.current = null;
      }
      observer.disconnect();
    };
  }, [slug, containerRef]);
}

function enqueueChunkInfo(slug: string, request: CitationChunkInfoRequest) {
  const cacheKey = getChunkInfoCacheKey(slug, request);
  if (chunkInfoCache.has(cacheKey) || inFlightChunkInfoKeys.has(cacheKey)) {
    return;
  }

  let pendingForSlug = pendingChunkInfoBySlug.get(slug);
  if (!pendingForSlug) {
    pendingForSlug = new Map();
    pendingChunkInfoBySlug.set(slug, pendingForSlug);
  }

  pendingForSlug.set(cacheKey, request);

  if (chunkInfoFlushTimer) {
    return;
  }

  chunkInfoFlushTimer = setTimeout(() => {
    chunkInfoFlushTimer = null;
    flushPendingChunkInfo();
  }, CHUNK_INFO_BATCH_DELAY_MS);
}

async function flushPendingChunkInfo() {
  const batches = Array.from(pendingChunkInfoBySlug.entries()).flatMap(([slug, pending]) => {
    const entries = Array.from(pending.entries());
    const slugBatches: Array<[string, typeof entries]> = [];
    for (let index = 0; index < entries.length; index += CHUNK_INFO_BATCH_SIZE) {
      slugBatches.push([slug, entries.slice(index, index + CHUNK_INFO_BATCH_SIZE)]);
    }
    return slugBatches;
  });

  pendingChunkInfoBySlug.clear();

  await Promise.all(
    batches.map(async ([slug, entries]) => {
      if (entries.length === 0) return;

      const requests = entries.map(([, request]) => ({
        document_guid: request.documentGuid,
        ...(request.kind === 'segment'
          ? { segment_index: request.index }
          : { chunk_id: request.index }),
      }));
      entries.forEach(([cacheKey]) => {
        inFlightChunkInfoKeys.add(cacheKey);
      });

      try {
        const response = await backendClient.post<CitationChunkInfo[]>(
          `/correspondence/${slug}/chunk-info`,
          requests
        );
        const cacheKeyByResponseKey = new Map(
          entries.map(([cacheKey, request]) => [getRequestKey(request), cacheKey]),
        );
        const returnedKeys = new Set<string>();

        response.data.forEach((chunkInfo) => {
          const responseKey = chunkInfo.segment_index !== undefined
            ? `${chunkInfo.document_guid}:s${chunkInfo.segment_index}`
            : `${chunkInfo.document_guid}:${chunkInfo.chunk_id}`;
          const cacheKey = cacheKeyByResponseKey.get(responseKey);
          if (!cacheKey) return;
          chunkInfoCache.set(cacheKey, chunkInfo);
          returnedKeys.add(cacheKey);
        });

        entries.forEach(([cacheKey]) => {
          if (!returnedKeys.has(cacheKey)) {
            chunkInfoCache.set(cacheKey, null);
          }
        });
      } catch (error) {
        console.debug('Failed to bulk fetch chunk info:', error);
        entries.forEach(([cacheKey]) => {
          chunkInfoCache.set(cacheKey, null);
        });
      } finally {
        entries.forEach(([cacheKey]) => {
          inFlightChunkInfoKeys.delete(cacheKey);
        });
        notifyChunkInfoSubscribers(slug);
      }
    })
  );
}

function subscribeToChunkInfo(slug: string, callback: () => void) {
  let subscribers = subscribersBySlug.get(slug);
  if (!subscribers) {
    subscribers = new Set();
    subscribersBySlug.set(slug, subscribers);
  }

  subscribers.add(callback);

  return () => {
    subscribers?.delete(callback);
    if (subscribers?.size === 0) {
      subscribersBySlug.delete(slug);
    }
  };
}

function notifyChunkInfoSubscribers(slug: string) {
  subscribersBySlug.get(slug)?.forEach((callback) => {
    callback();
  });
}

function applyChunkInfoToCitation(
  citation: HTMLElement,
  chunkInfo: CitationChunkInfo | null | undefined
) {
  if (!chunkInfo) {
    citation.setAttribute(CHUNK_INFO_STATUS_ATTR, CHUNK_INFO_MISSING);
    return;
  }
  citation.removeAttribute('title');
  citation.setAttribute(CHUNK_INFO_STATUS_ATTR, CHUNK_INFO_READY);
  citation.setAttribute('data-doc-guid', chunkInfo.document_guid);

  if (chunkInfo.display_name || chunkInfo.file_name) {
    citation.setAttribute(
      'data-file-name',
      documentDisplayName({ displayName: chunkInfo.display_name, fileName: chunkInfo.file_name }),
    );
  }

  if (chunkInfo.page_number !== undefined) {
    citation.setAttribute('data-page-number', String(chunkInfo.page_number));
  }

  if (chunkInfo.position_label) {
    citation.setAttribute('data-position', chunkInfo.position_label);
  }
}

function getChunkInfoCacheKey(slug: string, request: CitationChunkInfoRequest) {
  const locatorKey = request.kind === 'segment' ? `s${request.index}` : `c${request.index}`;
  return `${slug}:${request.documentGuid}:${locatorKey}`;
}

function getRequestKey(request: CitationChunkInfoRequest) {
  return request.kind === 'segment'
    ? `${request.documentGuid}:s${request.index}`
    : `${request.documentGuid}:${request.index}`;
}